/**
 * #380 stage 4.1 (#1900) — the permanent indicator's data source.
 * Design: .specify/specs/380-admin-permissions-billing/impersonation-design.md §5
 *
 * "Source: an onSnapshot on the admin's OWN permissionSubjects/{uid} — not
 * local state... Listener error or unknown state ⇒ a blocking banner...
 * never 'no banner'. Absence of the banner must mean the server said no
 * session." That is why this hook has three outcomes, not two: a listener
 * that has not resolved yet is neither "no session" nor "has a session".
 *
 * The stored `mode` is "as last written; the gate recomputes it" (§2.4) —
 * so `editUntil`/`maxUntil` are re-checked against the clock here too,
 * exactly like `functions/permissions/callable.js`'s own step 3, otherwise
 * the banner would keep showing EDIT for up to 30 minutes after the gate
 * itself has already fallen back to VIEW.
 */
import { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'

import { firestore } from '../services/firebase/app'

function toMillis(value) {
  if (value == null) return null
  if (typeof value.toMillis === 'function') return value.toMillis()
  return value
}

function computeEffectiveMode(session, now) {
  const editUntil = toMillis(session.editUntil)
  const maxUntil = toMillis(session.maxUntil)
  const stillEditable = session.mode === 'edit' && editUntil != null && editUntil > now
  const withinMaxAge = maxUntil == null || now < maxUntil
  return stillEditable && withinMaxAge ? 'edit' : 'view'
}

/**
 * @param {string|null|undefined} uid - the signed-in user's own uid (never a target's).
 * @return {{
 *   status: 'loading' | 'no-session' | 'active' | 'unknown',
 *   session?: object,
 *   effectiveMode?: 'view' | 'edit',
 *   msRemaining?: number | null,
 *   error?: Error,
 * }}
 */
export default function useImpersonationSession(uid) {
  const [state, setState] = useState({ status: 'loading' })
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!uid) {
      setState({ status: 'no-session' })
      return undefined
    }

    setState({ status: 'loading' })
    const ref = doc(firestore, 'permissionSubjects', String(uid))
    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        const session = snapshot.exists() ? snapshot.data()?.activeImpersonation : null
        setState(session == null ? { status: 'no-session' } : { status: 'active', session })
      },
      (error) => {
        // Never fall back to "no-session" here — an unreadable doc is not
        // the same fact as a doc that says there is no session (§5/§6).
        console.error('[useImpersonationSession] listener failed:', error)
        setState({ status: 'unknown', error })
      }
    )

    return unsubscribe
  }, [uid])

  useEffect(() => {
    if (state.status !== 'active') {
      return undefined
    }
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [state.status])

  if (state.status !== 'active') {
    return state
  }

  const editUntil = toMillis(state.session.editUntil)
  const effectiveMode = computeEffectiveMode(state.session, now)

  return {
    status: 'active',
    session: state.session,
    effectiveMode,
    msRemaining: effectiveMode === 'edit' && editUntil != null ? Math.max(0, editUntil - now) : null,
  }
}
